/* global globalThis */

const POLL_START_FLIGHTS_KEY = Symbol.for(
    "dreamytales.previewPollStartFlights"
);
const DEFAULT_SUCCESS_REUSE_MS = 5000;
const DEFAULT_NETWORK_RETRY_DELAY_MS = 750;

const createIdempotencyKey = () => {
    if (typeof globalThis.crypto?.randomUUID === "function") {
        return globalThis.crypto.randomUUID();
    }

    return [
        Date.now().toString(36),
        Math.random().toString(36).slice(2),
    ].join("-");
};

const isRetryableNetworkError = (error) => {
    const axiosCode = String(error?.code || "").trim().toUpperCase();
    const httpStatus = Number(error?.response?.status);
    const hasHttpStatus = Number.isFinite(httpStatus) && httpStatus > 0;

    return axiosCode === "ERR_NETWORK" && !hasHttpStatus;
};

const waitForRetryDelay = (delayMs) => {
    const normalizedDelayMs = Number(delayMs);
    if (!Number.isFinite(normalizedDelayMs) || normalizedDelayMs <= 0) {
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        globalThis.setTimeout(resolve, normalizedDelayMs);
    });
};

const startWithNetworkRetry = async (
    startRequest,
    retryDelayMs,
    idempotencyKey,
    hasActiveCaller
) => {
    try {
        return await startRequest({ idempotencyKey });
    } catch (error) {
        if (!isRetryableNetworkError(error)) {
            throw error;
        }

        await waitForRetryDelay(retryDelayMs);
        if (!hasActiveCaller()) {
            throw error;
        }

        return startRequest({ idempotencyKey });
    }
};

const getPollStartFlights = () => {
    if (!globalThis[POLL_START_FLIGHTS_KEY]) {
        globalThis[POLL_START_FLIGHTS_KEY] = new Map();
    }

    return globalThis[POLL_START_FLIGHTS_KEY];
};

const clearFlightEntry = (requestKey, entry) => {
    const pollStartFlights = getPollStartFlights();
    if (pollStartFlights.get(requestKey) !== entry) {
        return;
    }

    if (entry.cleanupTimer) {
        globalThis.clearTimeout(entry.cleanupTimer);
    }

    pollStartFlights.delete(requestKey);
};

/**
 * Shares the start request for identical preview jobs across every customizer
 * bundle instance in this browser tab.
 */
export function runPreviewPollStartOnce(
    requestKey,
    startRequest,
    {
        successReuseMs = DEFAULT_SUCCESS_REUSE_MS,
        networkRetryDelayMs = DEFAULT_NETWORK_RETRY_DELAY_MS,
        isActive = () => true,
    } = {}
) {
    const pollStartFlights = getPollStartFlights();
    const existingEntry = pollStartFlights.get(requestKey);
    if (existingEntry) {
        existingEntry.activeCallers.add(isActive);
        return existingEntry.promise;
    }

    const entry = {
        promise: null,
        cleanupTimer: null,
        activeCallers: new Set([isActive]),
    };
    const requestPromise = Promise.resolve().then(() => startWithNetworkRetry(
        startRequest,
        networkRetryDelayMs,
        createIdempotencyKey(),
        () => [...entry.activeCallers].some((isCallerActive) => isCallerActive())
    ));
    entry.promise = requestPromise;
    pollStartFlights.set(requestKey, entry);

    requestPromise.then(
        () => {
            if (successReuseMs <= 0) {
                clearFlightEntry(requestKey, entry);
                return;
            }

            entry.cleanupTimer = globalThis.setTimeout(
                () => clearFlightEntry(requestKey, entry),
                successReuseMs
            );
        },
        () => clearFlightEntry(requestKey, entry)
    );

    return requestPromise;
}

export function clearPreviewPollStart(requestKey) {
    const entry = getPollStartFlights().get(requestKey);
    if (entry) {
        clearFlightEntry(requestKey, entry);
    }
}
